import type { ReactNode } from 'react'
import { Button, Skeleton } from '@heroui/react'

import { errorMessage } from '@/lib/api/api-error'

/**
 * The three states every list screen in the Back Office passes through, written
 * once.
 *
 * Products grew its own copies of all three inline, and the ten modules that
 * followed would each have grown another — at which point "could not load" reads
 * differently on every screen. The wording and the shape live here; a page
 * supplies only what is specific to it.
 */

export function LoadingState({ className = 'h-96' }: { className?: string }) {
  return <Skeleton className={`${className} w-full rounded-2xl`} />
}

interface ErrorStateProps {
  /** What failed, in the merchant's terms — "your banners", "this order". */
  subject: string
  error: unknown
  onRetry: () => void
}

/**
 * A failed list must never read as an empty one: a merchant who is told they
 * have no products goes looking for products they still have.
 */
export function ErrorState({ subject, error, onRetry }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className="border-danger/40 flex flex-col items-center gap-3 rounded-2xl border border-dashed px-6 py-16 text-center"
    >
      <p className="font-medium">Could not load {subject}.</p>
      <p className="text-muted max-w-sm text-sm">
        {errorMessage(error, 'The server did not answer. Check your connection.')}
      </p>
      <Button
        variant="ghost"
        size="sm"
        className="border-border mt-1 h-9 rounded-full border px-4"
        onPress={onRetry}
      >
        Try again
      </Button>
    </div>
  )
}

interface EmptyStateProps {
  title: string
  description: string
  action?: { label: string; onPress: () => void }
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="border-border flex flex-col items-center gap-3 rounded-2xl border border-dashed px-6 py-16 text-center">
      <p className="font-medium">{title}</p>
      <p className="text-muted max-w-sm text-sm">{description}</p>
      {action && (
        <Button
          variant="ghost"
          size="sm"
          className="border-border mt-1 h-9 rounded-full border px-4"
          onPress={action.onPress}
        >
          {action.label}
        </Button>
      )}
    </div>
  )
}

interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
}

/**
 * Previous/Next over a count, rather than numbered pages.
 *
 * The API answers with an offset and a total and no notion of a page, and a
 * merchant working through a list moves one step at a time — numbering would be
 * more chrome for the same two actions.
 */
export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const first = total === 0 ? 0 : page * pageSize + 1
  const last = Math.min(total, (page + 1) * pageSize)

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-muted text-xs tabular-nums">
        {first}–{last} of {total}
      </p>

      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="border-border h-8 rounded-full border px-3 text-xs"
          isDisabled={page === 0}
          onPress={() => onPageChange(Math.max(0, page - 1))}
        >
          Previous
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="border-border h-8 rounded-full border px-3 text-xs"
          isDisabled={last >= total}
          onPress={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

interface SectionProps {
  title: string
  description?: string
  action?: ReactNode
  children: ReactNode
}

/** A titled block inside a settings-style page — Business, Hours, Shipping. */
export function Section({ title, description, action, children }: SectionProps) {
  return (
    <section className="border-border bg-surface flex flex-col gap-4 rounded-2xl border p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold tracking-[-0.01em]">{title}</h2>
          {description && <p className="text-muted text-sm">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}
